<template>
	<div>
		<div>
			<h2 class="mb-4 font-bold">{{ type }} {{ $t('Link') }}</h2>

			<div class="mb-4">
				<label class="block mb-2" for="name">{{ $t('Nombre') }}</label>
				<input
					type="text"
					v-model="link.name"
					id="name"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				/>
				<div v-if="$v.link.name.$dirty">
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.name.required">
						{{ $t('name_required') }}
					</div>
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.name.maxLength">
						{{ $t('name_validation') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="country_id">País</label>
				<select
					v-model="link.country_id"
					ref="country_id"
					id="country_id"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				>
					<option
						v-for="(country, index) in countries"
						:value="country.id"
						:key="index"
					>
						{{ country.name }}
					</option>
				</select>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="url">{{ $t('Url') }}</label>
				<input
					type="url"
					v-model="link.url"
					id="url"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				/>
				<div v-if="$v.link.url.$dirty">
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.url.required">
						{{ $t('url_required') }}
					</div>
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.url.maxLength">
						{{ $t('url_validation') }}
					</div>
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.url.url">
						{{ $t('url_invalid') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="order">{{ $t('Orden') }}</label>
				<input
					type="number"
					v-model="link.order"
					id="order"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				/>
				<div v-if="$v.link.order.$dirty">
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.order.required">
						{{ $t('orden_required') }}
					</div>
					<div class="text-sm text-red-500 mt-2" v-if="!$v.link.order.integer">
						{{ $t('orden_validation') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2">{{ $t('publication_date') }}</label>
				<datepicker
					v-model="link.publishing_date"
					:full-month-name="true"
					:language="es"
					:inline="true"
					:format="dateFormat"
					:disabled-dates="disabledDates"
					:monday-first="true"
				>
				</datepicker>
				<div v-if="$v.link.publishing_date.$dirty">
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.link.publishing_date.required"
					>
						{{ $t('publication_date_required') }}
					</div>
				</div>
			</div>

			<label>{{ $t('Roles') }}</label>
			<dual-list
				v-if="DualListOptions"
				id="vueduallist"
				:options="DualListOptions"
			/>
			<div v-if="$v.DualListOptions.selectedItems.$dirty">
				<div
					class="text-sm text-red-500 mt-2"
					v-if="!$v.DualListOptions.selectedItems.required"
				>
					{{ $t('roles_required') }}
				</div>
			</div>

			<div class="mt-4 flex justify-end">
				<ButtonBlack @click.native="submit">
					<div v-if="loading">
						<pulse-loader
							:loading="loading"
							:color="'#fff'"
							:size="'10px'"
						></pulse-loader>
					</div>
					<div v-else>
						{{ buton_text }}
					</div>
				</ButtonBlack>
			</div>

			<div v-if="error" class="mt-4">
				<p class="text-sm">{{ $t('server_error') }}</p>
				<p class="text-sm text-red-500" v-html="server_error"></p>
			</div>
		</div>
	</div>
</template>

<script>
import { single } from '@/mixins'
import DualList from '@/components/DualList.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import datepicker from 'vuejs-datepicker'
import { es } from 'vuejs-datepicker/dist/locale'
import { required, maxLength, integer, url } from 'vuelidate/lib/validators'
import { fetchCountriesSelect } from '@/apollo/countries'
import { fetchLink, updateLink, insertLink } from '@/apollo/links'
import { fetchRolesSelect } from '@/apollo/roles'

export default {
	mixins: [single],
	components: {
		datepicker,
		DualList,
		PulseLoader
	},
	data() {
		return {
			disabledDates: {
				to: this.id ? null : this.$moment().subtract(1, 'days').toDate()
			},
			link: {
				name: '',
				country_id: null,
				url: '',
				order: '',
				publishing_date: null,
				roles: this.id ? null : []
			},
			roles: null,
			countries: [],
			es
		}
	},
	apollo: {
		roles: fetchRolesSelect(),
		countries: fetchCountriesSelect(),
		link() {
			const query = fetchLink(this.id)
			query.skip = this.id == null
			return query
		}
	},
	watch: {
		roles: function (data) {
			if (this.link.roles !== null && this.DualListOptions === null) {
				this.loadRolesList(data, this.link.roles)
			}
		},
		link: function (data) {
			if (
				this.id != null &&
				this.roles !== null &&
				this.DualListOptions === null
			) {
				this.loadRolesList(this.roles, data.roles)
			}
		}
	},
	methods: {
		dateFormat() {
			return 'YYYY-MM-DD'
		},
		loadRolesList(roles, selected) {
			this.loadDualList(
				roles,
				selected
					? selected
							.map(org => roles.find(elem => elem.id == org.id))
							.filter(elem => elem != null)
					: selected
			)
		},
		updateSingleLink() {
			const mutationData = {
				linkId: this.id,
				linkData: { ...this.link, roles: undefined },
				linkRoles: this.DualListOptions.selectedItems
			}
			return this.$apollo.mutate(updateLink(mutationData))
		},
		insertSingleLink() {
			const mutationData = {
				linkData: { ...this.link, roles: undefined },
				linkRoles: this.DualListOptions.selectedItems
			}
			return this.$apollo.mutate(insertLink(mutationData))
		},
		submit() {
			this.startLoadingState()
			this.$v.$touch()
			if (!this.$v.$invalid) {
				;(this.id ? this.updateSingleLink() : this.insertSingleLink())
					.then(() => this.stopLoadingState())
					.catch(error => this.setError(error))
			} else {
				this.setError(this.$t('form_validation'))
			}
		}
	},
	validations() {
		return {
			link: {
				name: {
					required,
					maxLength: maxLength(100)
				},
				url: {
					required,
					maxLength: maxLength(200),
					url
				},
				order: {
					required,
					integer
				},
				publishing_date: {
					required
				}
			},
			DualListOptions: {
				selectedItems: {
					required
				}
			}
		}
	}
}
</script>
